import {css, StyleSheet} from 'aphrodite';

import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {isCoreObjectId} from 'app-core/framework/CoreObjectIntrospectionUtils';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import isArchived from 'archive/isArchived';
import sortArchivedToLast from 'archive/sortArchivedToLast';
import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {InventoryFeature, LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {
  LegacyAssaySchemaType,
  TagSchemaSequenceSchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isNodeTesting} from 'lib/Browser';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import getFeatureStatusAwareRequiredLinkFolderItemTypes from 'schemas/fields/getFeatureStatusAwareRequiredLinkFolderItemTypes';
import {
  ANY_ENTITY,
  ComputedFieldType,
  FieldOption,
  FieldOptionHeader,
  FieldType,
  getRequiredLinkFolderItemType,
  getRequiredLinkTagSchema,
  LeafFieldOption,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import getSchemaInterfaceType from 'schemas/schema-interface/v2/getSchemaInterfaceType';
import getSchemaInterfaceMetadata from 'schemas/schema-interface/v2/menu/getSchemaInterfaceMetadata';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {toModelTypeConstant} from 'tag-schema/util/ModelTypeConversions';
import {oligoAwareFolderItemTypeMatchesTagSchema} from 'tag-schema/util/TagSchemaUtil';
import * as Colors from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import APIUtil from 'util/APIUtil';
import {capwords, capitalize, typeToName, typeToPluralized} from 'util/NameUtil';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

function filterForEntityTagSchemas(
  tagSchemas: Array<TagSchemaSummary>,
  initialField?: SchemaField
): Array<TagSchemaSummary> {
  const requiredLinkTagSchema = initialField != null ? getRequiredLinkTagSchema(initialField) : null;
  const requiredLinkTagSchemaId = requiredLinkTagSchema && requiredLinkTagSchema.id;
  return tagSchemas.filter(
    (tagSchema) =>
      getFeatureStatusAwareRequiredLinkFolderItemTypes().includes(tagSchema.folderItemType) ||
      // In case the mixtures capability is disabled, always add the initially selected schema
      tagSchema.id === requiredLinkTagSchemaId
  );
}

function filterForEntryTagSchemas(tagSchemas: Array<TagSchemaSummary>): Array<TagSchemaSummary> {
  return tagSchemas.filter((tagSchema) => tagSchema.folderItemType === ModelTypeConstants.ENTRY);
}

function getAvailableFolderItemTypes(initialField?: SchemaField): Array<string> {
  // BFIs, Proteins, Sequences, and maybe Mixtures
  const availableFileTypes = getFeatureStatusAwareRequiredLinkFolderItemTypes();

  if (!initialField) {
    return availableFileTypes;
  }

  const requiredLinkFolderItemType = getRequiredLinkFolderItemType(initialField);
  // Add the mixture link type if the mixtures capability is disabled but mixture was already selected
  if (
    requiredLinkFolderItemType &&
    requiredLinkFolderItemType === ModelTypeConstants.MIXTURE &&
    !availableFileTypes.includes(requiredLinkFolderItemType)
  ) {
    availableFileTypes.push(requiredLinkFolderItemType);
  }

  return availableFileTypes;
}

function sortStorableSchemasByType({
  storableSchemas,
}: {
  storableSchemas: Array<StorableSchema>;
}): Array<StorableSchema> {
  let containerSchemas: Array<StorableSchema> = [];
  let plateSchemas: Array<StorableSchema> = [];
  let boxSchemas: Array<StorableSchema> = [];
  let locationSchemas: Array<StorableSchema> = [];

  for (const schema of storableSchemas) {
    if (APIUtil.isPlateSchema(schema.id)) {
      plateSchemas.push(schema);
    } else if (APIUtil.isContainerSchema(schema.id)) {
      containerSchemas.push(schema);
    } else if (APIUtil.isBoxSchema(schema.id)) {
      boxSchemas.push(schema);
    } else if (APIUtil.isLocationSchema(schema.id)) {
      locationSchemas.push(schema);
    } else {
      assertAndContinue(false, 'Expected storable schema');
    }
  }
  containerSchemas = sortArchivedToLast(containerSchemas);
  plateSchemas = sortArchivedToLast(plateSchemas);
  boxSchemas = sortArchivedToLast(boxSchemas);
  locationSchemas = sortArchivedToLast(locationSchemas);

  return [...plateSchemas, ...containerSchemas, ...boxSchemas, ...locationSchemas];
}

// These attributes are only used for some field types, and we need to make sure they are null so these are
// correctly cleared when changing types. Eg: Selector -> Text type should null out schemaFieldSelectorId
const FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS = {
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const LOADING_FIELD_OPTION: LeafFieldOption = {
  ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
  label: 'Loading',
  requiredLink: null,
  fieldDefinition: FieldType.TEXT,
  disabled: true,
};

export function getSnapshotFieldOption(): LeafFieldOption {
  return {
    ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
    fieldDefinition: ComputedFieldType.SNAPSHOT,
    label: 'Computed field (snapshot)',
    icon: 'function',
  };
}

function entitySubmenu({
  initialField,
  includeGenericBioentityLinks,
  tagSchemas,
  schemaInterfaces,
  includeCoreTypeLinks = true,
}: {
  initialField?: SchemaField;
  includeGenericBioentityLinks: boolean;
  tagSchemas: Array<TagSchemaSummary>;
  schemaInterfaces: Array<SchemaInterface>;
  includeCoreTypeLinks?: boolean;
}): Array<FieldOption> {
  const anyEntityOptions = includeGenericBioentityLinks
    ? [
        {
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.BIOENTITY_LINK,
          requiredLink: {
            folderItemType: null,
            tagSchema: null,
            entitySchemaFieldPath: null,
            schemaInterface: null,
          },
          label: ANY_ENTITY.name,
          icon: FolderItemIcon.BasicFolderItem,
        } satisfies FieldOption,
      ]
    : [];
  const coreTypeOptions = includeCoreTypeLinks
    ? getAvailableFolderItemTypes(initialField).map(
        (folderItemType): LeafFieldOption => ({
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.BIOENTITY_LINK,
          requiredLink: {
            folderItemType,
            tagSchema: null,
            entitySchemaFieldPath: null,
            schemaInterface: null,
          },
          label: capitalize(typeToName(folderItemType)),
          icon: folderItemType as BenchlingIconName,
        })
      )
    : [];

  const filteredSchemaInterfaces = BenchlingConfig.SCHEMAS_ENABLE_INTERFACES
    ? schemaInterfaces
    : schemaInterfaces.filter((schemaInterface): boolean => {
        // TODO(BNCH-92241): Remove this and always crash once we generate valid ids in GQL fixtures / mocks
        if (!isCoreObjectId(schemaInterface.id)) {
          assert(isNodeTesting, 'Should not see invalid interface IDs outside of tests');
          return false;
        }
        return getSchemaInterfaceType(schemaInterface.id) === DomainGraphType.STATIC_SCHEMA_INTERFACE;
      });

  const interfaceOptions = sortArchivedToLast(filteredSchemaInterfaces).map(
    (schemaInterface): LeafFieldOption => ({
      fieldDefinition: FieldType.BIOENTITY_LINK,
      requiredLink: {
        schemaInterface,
        folderItemType: null,
        tagSchema: null,
        entitySchemaFieldPath: null,
      },
      label: getArchiveAwareDisplayName(schemaInterface),
      // TODO(BNCH-92328): Allow null here
      metadata: getSchemaInterfaceMetadata(schemaInterface.id) ?? undefined,
      icon: ModelTypeConstants.SCHEMA_INTERFACE,
    })
  );

  const schemaOptions = filterForEntityTagSchemas(sortArchivedToLast(tagSchemas), initialField).map(
    (schema): LeafFieldOption => ({
      ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
      fieldDefinition: FieldType.BIOENTITY_LINK,
      requiredLink: {
        tagSchema: schema,
        folderItemType: null,
        entitySchemaFieldPath: null,
        schemaInterface: null,
      },
      label: getArchiveAwareDisplayName(schema),
      icon: schema.iconId,
    })
  );
  const allOptions: Array<LeafFieldOption | FieldOptionHeader> = [];

  for (const {sectionLabel, options} of [
    {sectionLabel: null, options: anyEntityOptions},
    {sectionLabel: null, options: coreTypeOptions},
    {
      sectionLabel: capwords(typeToPluralized(ModelTypeConstants.SCHEMA_INTERFACE)),
      options: interfaceOptions,
    },
    {sectionLabel: 'Schemas', options: schemaOptions},
  ]) {
    if (options.length) {
      const optionsForAppend: Array<LeafFieldOption | FieldOptionHeader> = [];
      if (sectionLabel != null) {
        optionsForAppend.push({
          label: sectionLabel,
          isHeader: true,
        });
      }
      optionsForAppend.push(...options);
      if (allOptions.length) {
        optionsForAppend[0].extraClasses = css(styles.separatorAbove);
      }
      allOptions.push(...optionsForAppend);
    }
  }

  return allOptions;
}
export function getInterfaceFieldOptions({
  initialField,
  schemaInterfaces: allSchemaInterfaces,
  includeGenericBioentityLinks,
  includeArchivedSchemaInterfaces,
}: {
  initialField?: SchemaField;
  schemaInterfaces: Array<SchemaInterface>;
  includeGenericBioentityLinks?: boolean;
  includeArchivedSchemaInterfaces?: boolean;
}): Array<FieldOption> {
  const schemaInterfaces =
    (includeArchivedSchemaInterfaces
      ? allSchemaInterfaces
      : allSchemaInterfaces?.filter((schemaInterface) => !isArchived(schemaInterface))) ?? [];

  return [
    {
      label: 'Entity',
      icon: FolderItemIcon.BasicFolderItem,
      submenu: entitySubmenu({
        initialField,
        includeGenericBioentityLinks:
          includeGenericBioentityLinks || BenchlingConfig.SCHEMAS_ENABLE_GENERIC_ENTITY_LINK_EVERYWHERE,
        tagSchemas: [],
        schemaInterfaces,
        includeCoreTypeLinks: false,
      }),
    },
  ];
}

export interface GetFieldOptionsArgs {
  initialField?: SchemaField;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  assaySchemas?: Array<AssaySchema>;
  storableSchemas: Array<StorableSchema>;
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
  schemaInterfaces?: Array<SchemaInterface>;
  includeJSONField?: boolean;
  includeAssayLinks?: boolean;
  includeBatchLinks?: boolean;
  includeGenericBioentityLinks?: boolean;
  includeBooleanField?: boolean;
  includeSnapshotField?: boolean;
  includeSequenceLinks?: boolean; // DNA + RNA sequences
  includeDnaSequenceSpecificLinks?: boolean; // DNA sequences only
  includeEntrySchemas?: boolean;
  includeEntryLinks?: boolean;
  snapshotFieldDisabledMessage?: string;
  includeArchivedSchemas?: boolean;
  sequenceSchemaType?: TagSchemaSequenceSchemaType | null;
}

export function getFieldOptions({
  initialField,
  schemaFieldSelectors,
  assaySchemas: allAssaySchemas,
  storableSchemas: allStorableSchemas,
  tagSchemas: allTagSchemas,
  schemaInterfaces: allSchemaInterfaces,
  includeJSONField,
  includeAssayLinks,
  includeBatchLinks,
  includeGenericBioentityLinks,
  includeBooleanField,
  includeSnapshotField,
  includeSequenceLinks,
  includeDnaSequenceSpecificLinks,
  includeArchivedSchemas = true,
  includeEntrySchemas = false,
  includeEntryLinks = true,
  snapshotFieldDisabledMessage,
  sequenceSchemaType,
}: GetFieldOptionsArgs): Array<FieldOption> {
  const unarchivedSchemaFieldSelectors = schemaFieldSelectors.filter((sfs) => !isArchived(sfs));
  const assaySchemas = includeArchivedSchemas
    ? allAssaySchemas
    : allAssaySchemas?.filter((schema) => !isArchived(schema));
  const storableSchemas = includeArchivedSchemas
    ? allStorableSchemas
    : allStorableSchemas?.filter((schema) => !isArchived(schema));
  const tagSchemas = includeArchivedSchemas
    ? allTagSchemas
    : allTagSchemas?.filter((schema) => !isArchived(schema));
  const schemaInterfaces =
    (includeArchivedSchemas
      ? allSchemaInterfaces
      : allSchemaInterfaces?.filter((schemaInterface) => !isArchived(schemaInterface))) ?? [];

  const resultSchemas = (assaySchemas ?? []).filter(
    (assaySchema) => assaySchema.schemaType === LegacyAssaySchemaType.ASSAY_RESULT
  );
  const requestSchemas = (assaySchemas ?? []).filter(
    (assaySchema) => assaySchema.schemaType === LegacyAssaySchemaType.ASSAY_REQUEST
  );
  const runSchemas = (assaySchemas ?? []).filter(
    (assaySchema) => assaySchema.schemaType === LegacyAssaySchemaType.ASSAY_RUN
  );
  let sequenceSubtype: SequenceSubtype;
  if (sequenceSchemaType === TagSchemaSequenceSchemaType.RNA_SEQUENCE) {
    sequenceSubtype = ModelTypeConstants.RNA_SEQUENCE;
  } else {
    sequenceSubtype = ModelTypeConstants.SEQUENCE;
  }
  const partLinkableTagSchemas = sortArchivedToLast(tagSchemas ?? []).filter((tagSchema) =>
    oligoAwareFolderItemTypeMatchesTagSchema(sequenceSubtype, tagSchema)
  );
  const translationLinkableTagSchemas = sortArchivedToLast(tagSchemas ?? []).filter(
    ({folderItemType}) => toModelTypeConstant(folderItemType) === ModelTypeConstants.PROTEIN
  );
  const transcriptionLinkableTagSchemas = sortArchivedToLast(tagSchemas ?? []).filter((tagSchema) =>
    oligoAwareFolderItemTypeMatchesTagSchema(ModelTypeConstants.RNA_SEQUENCE, tagSchema)
  );

  const numericFields: FieldOption = {
    label: 'Number',
    icon: 'number',
    submenu: [
      {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.INTEGER, label: 'Integer'},
      {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.FLOAT, label: 'Decimal'},
    ],
  };

  if (initialField?.unitApiIdentifier != null) {
    return [numericFields];
  }

  const allOptions: Array<FieldOption> = [
    {
      label: 'Text',
      icon: 'text',
      submenu: [
        {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.TEXT, label: 'Text'},
        {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.LONG_TEXT, label: 'Long text'},
      ],
    },
    numericFields,
    {
      label: 'Date',
      icon: 'calendar',
      submenu: [
        {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.DATE, label: 'Date'},
        {...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS, fieldDefinition: FieldType.DATETIME, label: 'Datetime'},
      ],
    },
  ];

  if (includeBooleanField) {
    allOptions.push({
      ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
      fieldDefinition: FieldType.BOOLEAN,
      label: 'Boolean',
      icon: 'boolean',
    });
  }

  if (unarchivedSchemaFieldSelectors.length) {
    allOptions.push({
      icon: 'multi-select',
      label: 'Dropdown',
      submenu: unarchivedSchemaFieldSelectors.map((selector) => ({
        ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
        fieldDefinition: FieldType.SELECTOR,
        schemaFieldSelectorId: selector.id,
        label: selector.name,
      })),
    });
  }

  if (includeJSONField) {
    allOptions.push({
      ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
      fieldDefinition: FieldType.JSON,
      icon: 'json',
      label: 'JSON',
    });
  }

  allOptions.push({
    ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
    fieldDefinition: FieldType.BLOB_LINK,
    icon: 'attachment',
    label: 'Attachment',
    extraClasses: css(styles.separatorBelow),
  });

  // Entry
  if (includeEntryLinks) {
    allOptions.push({
      label: 'Entry',
      icon: FolderItemIcon.Entry,
      ...(includeEntrySchemas
        ? {
            submenu: filterForEntryTagSchemas(tagSchemas as unknown as Array<TagSchemaSummary>).map(
              (schema): LeafFieldOption => ({
                ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
                fieldDefinition: FieldType.ENTRY_LINK,
                requiredLink: {
                  tagSchema: schema,
                  folderItemType: null,
                  entitySchemaFieldPath: null,
                  schemaInterface: null,
                },
                label: getArchiveAwareDisplayName(schema),
                icon: schema.iconId,
              })
            ),
          }
        : {
            ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
            fieldDefinition: FieldType.ENTRY_LINK,
          }),
    });
  }

  // Entity
  allOptions.push({
    label: 'Entity',
    icon: FolderItemIcon.BasicFolderItem,
    submenu: entitySubmenu({
      initialField,
      includeGenericBioentityLinks:
        includeGenericBioentityLinks || BenchlingConfig.SCHEMAS_ENABLE_GENERIC_ENTITY_LINK_EVERYWHERE,
      tagSchemas: tagSchemas as unknown as Array<TagSchemaSummary>,
      schemaInterfaces,
    }),
  });

  // Inventory
  if (storableSchemas.length > 0 && isFeatureEnabled({feature: InventoryFeature.INVENTORY_DISCOVER})) {
    allOptions.push({
      label: 'Inventory',
      icon: 'fridge',
      submenu: [
        {
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.STORABLE_LINK,
          requiredLink: {storableSchema: null},
          icon: 'fridge',
          label: 'Any inventory',
          extraClasses: css(styles.separatorBelow),
        },
        ...sortStorableSchemasByType({storableSchemas}).map(
          (schema): LeafFieldOption => ({
            ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
            label: getArchiveAwareDisplayName(schema),
            icon: schema.iconId,
            fieldDefinition: FieldType.STORABLE_LINK,
            requiredLink: {storableSchema: schema},
          })
        ),
      ],
    });
  }

  // Result
  if (includeAssayLinks && BenchlingConfig.RESULTS_ENABLE_CREATE_RESULT_LINK_FIELDS) {
    allOptions.push({
      label: 'Result',
      icon: ModelTypeConstants.RESULT_SCHEMA,
      submenu: [
        {
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_RESULT_LINK,

          label: 'Any result',
          extraClasses: css(styles.separatorBelow),
        },
        ...sortArchivedToLast(resultSchemas).map((schema) => ({
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_RESULT_LINK,
          requiredLink: {assaySchema: schema},
          label: getArchiveAwareDisplayName(schema),
        })),
      ],
    });
  }

  // Request
  if (includeAssayLinks && BenchlingConfig.ENABLE_REQUESTS) {
    allOptions.push({
      label: 'Request',
      icon: ModelTypeConstants.REQUEST_SCHEMA,
      submenu: [
        {
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_REQUEST_LINK,

          label: 'Any request',
          extraClasses: css(styles.separatorBelow),
        },
        ...sortArchivedToLast(requestSchemas).map((schema) => ({
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_REQUEST_LINK,
          requiredLink: {assaySchema: schema},
          label: getArchiveAwareDisplayName(schema),
        })),
      ],
    });
  }

  // Run
  if (includeAssayLinks && areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}])) {
    allOptions.push({
      label: 'Run',
      icon: ModelTypeConstants.RUN_SCHEMA,
      submenu: [
        {
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_RUN_LINK,
          label: 'Any run',
          extraClasses: css(styles.separatorBelow),
        },
        ...sortArchivedToLast(runSchemas).map((schema) => ({
          ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
          fieldDefinition: FieldType.ASSAY_RUN_LINK,
          requiredLink: {assaySchema: schema},
          label: getArchiveAwareDisplayName(schema),
        })),
      ],
    });
  }

  // Part
  if (includeSequenceLinks && partLinkableTagSchemas.length > 0) {
    allOptions.push({
      label: 'Part',
      icon: 'part',
      submenu: partLinkableTagSchemas.map((schema) => ({
        ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
        fieldDefinition: FieldType.PART_LINK,
        label: getArchiveAwareDisplayName(schema),
        requiredLink: {
          tagSchema: schema as unknown as TagSchemaSummary,
          folderItemType: null,
          entitySchemaFieldPath: null,
          schemaInterface: null,
        },
      })),
    });
  }

  // Translation
  if (includeSequenceLinks && translationLinkableTagSchemas.length > 0) {
    allOptions.push({
      label: 'Translation',
      icon: 'translation',
      submenu: translationLinkableTagSchemas.map((schema) => ({
        ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
        fieldDefinition: FieldType.TRANSLATION_LINK,
        label: getArchiveAwareDisplayName(schema),
        requiredLink: {
          tagSchema: schema as unknown as TagSchemaSummary,
          folderItemType: null,
          entitySchemaFieldPath: null,
          schemaInterface: null,
        },
      })),
    });
  }

  // Transcription
  if (includeDnaSequenceSpecificLinks && transcriptionLinkableTagSchemas.length > 0) {
    allOptions.push({
      label: 'Transcription',
      icon: FolderItemIcon.RNASequence,
      submenu: transcriptionLinkableTagSchemas.map((schema) => ({
        ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
        fieldDefinition: FieldType.TRANSCRIPTION_LINK,
        label: getArchiveAwareDisplayName(schema),
        requiredLink: {
          tagSchema: schema as unknown as TagSchemaSummary,
          folderItemType: null,
          entitySchemaFieldPath: null,
          schemaInterface: null,
        },
      })),
    });
  }

  if (includeSnapshotField) {
    allOptions.push({
      ...FIELD_OPTIONS_SPECIAL_VALUE_DEFAULTS,
      extraClasses: css(styles.separatorAbove),
      ...getSnapshotFieldOption(),
      disabled: Boolean(snapshotFieldDisabledMessage),
      disabledMessage: snapshotFieldDisabledMessage,
    });
  }

  return allOptions;
}

// Exporting only for testing purposes
export const styles = StyleSheet.create({
  separatorAbove: {
    borderTopColor: Colors.GRAY_LIGHT,
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
  },
  separatorBelow: {
    borderBottomColor: Colors.GRAY_LIGHT,
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
  },
});
